import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import _, { lastIndexOf } from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";
import Truncate from 'react-truncate';
import { EditorContext } from '../../index';
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { selectWidget, toggleEditMode, updateWidgetProperty, updateWidgetEditorState, deleteWidgets, cloneWidgets, bulkUpdateWidgetOrder, bulkUpdateWidgetLayouts, actionGroup, recalculateSectionColumnWidths, updateWidgetName, arrangeCarouselSlides, toggleHotspotView, splitPageBySelection } from 'actions/editor';
import { Property } from '../../components/properties';
import StoryboardContext from 'components/Storyboard/context';
import { getParentContainerOfType, getWidgetType, isWidgetChildrensHasHotspotChild, isWidgetHasHotspotChild } from 'components/Editor/helper/widget';
import { CAROUSEL_CARD, CONTENT_CARD, GROUP, HOTSPOT, PAGE, SECTION_COLUMN, CONTAINER,SLIDE } from 'constants/widgetTypes';
import { CAROUSEL_ARROWS, CAROUSEL_DOTS, CAROUSEL_TRANSITION } from 'constants/carousels';
import { breakpoints } from 'constants/breakpoints';
import { createWidget } from 'actions/editor';
import { isWidgetLocked, useChildrenWidgetSelected, useContainerSelector, usePageWidgetSelector, useSelectedWidgetSelector, useWidgetChildrenSelector } from 'components/Editor/hooks/useEditorSession';

import { Form, Tab, Tabs, Button, OverlayTrigger, Popover } from "react-bootstrap";

import { getBoundingRectFromLayout } from 'components/Editor/helper/layoutManager';
import { Background } from 'components/Editor/components/properties/background';
import { BackgroundView } from '../background';

import { BrandComponentProperty } from 'components/Editor/components/brandElements';
import userRoleService from 'services/userRoles.service';
import { useMakeFrame } from 'components/Editor/hooks/useMakeFrame';
import { INTERACTIVE_CONTENT_TYPES, PAGE_CONTAIN_STORYBOARDS, isFlexModeAllowed } from 'constants/storyboardType';
import { getCardFlexConfig } from './card/flexConfig';
import { MeasurementContext } from './measurementContext';
import { FlexConfigControls, LayoutConfigPopup, useFlexConfigDismissOnClickOutside } from '../flex/flexConfig';
import { pxToNumber } from '../flex/flexContext';
import { resolvePadding } from '../flex/flexCss';
import { resyncMoveableTargets } from '../flex/flexMeasure';
import reduxStore from '../../../../store.jsx';
import styles from './carousel.module.scss';

const state = {
  isFreeSize: false,
  breakPointType: 'desktop'
};

const CAROUSEL_EMPTY_FLOOR = 200;

function slideContentWidth(slideWidget, breakpoint, carouselEl, horizontalPadding) {
  let box = 0;
  if (breakpoint === 'mobile') {
    box = breakpoints.mobile.width;
  } else {
    const carouselWidth = carouselEl ? carouselEl.offsetWidth : 0;
    const minWidth = pxToNumber(_.get(slideWidget, `properties.${breakpoint}.container.minWidth`));
    if (carouselWidth > 0 && minWidth > 0) box = Math.min(carouselWidth, minWidth);
    else box = carouselWidth > 0 ? carouselWidth : minWidth;
  }
  return Math.max(0, box - (horizontalPadding || 0));
}

function packRowWrapHeight(items, contentWidth, gap) {
  let total = 0;
  let lineWidth = 0;   // width consumed on the current line (items + gaps)
  let lineHeight = 0;  // tallest item on the current line
  let lineCount = 0;

  items.forEach((item) => {
    const w = Math.min(item.width > 0 ? item.width : contentWidth, contentWidth);
    const needed = lineWidth === 0 ? w : lineWidth + gap + w;
    if (lineWidth > 0 && needed > contentWidth) {
      total += lineHeight;
      lineCount += 1;
      lineWidth = w;
      lineHeight = item.height;
    } else {
      lineWidth = needed;
      lineHeight = Math.max(lineHeight, item.height);
    }
  });
  if (lineWidth > 0) {
    total += lineHeight;
    lineCount += 1;
  }
  return total + gap * Math.max(0, lineCount - 1);
}

function estimateSlideContentHeight(slideWidget, slideChildren, breakpoint, carouselEl) {
  if (!slideChildren || slideChildren.length === 0) return 0;

  const { flexProperty } = getCardFlexConfig(slideWidget, breakpoint);
  const padding = resolvePadding(flexProperty.padding);
  const gap = pxToNumber(flexProperty.gap) || 0;

  const dims = _.orderBy(slideChildren, ['order'], ['asc']).map((child) => {
    const rect = getBoundingRectFromLayout(_.get(child, `properties.${breakpoint}.layout`));
    return { width: _.get(rect, 'width') || 0, height: _.get(rect, 'height') || 0 };
  });

  const column = _.startsWith(flexProperty.flexDirection, 'column');
  let content;
  if (column) {
    content = _.sumBy(dims, 'height') + gap * (dims.length - 1);
  } else {
    const wraps = flexProperty.flexWrap !== 'nowrap';
    const contentWidth = wraps
      ? slideContentWidth(slideWidget, breakpoint, carouselEl, (padding.topRight || 0) + (padding.bottomLeft || 0))
      : 0;
    content = (wraps && contentWidth > 0)
      ? packRowWrapHeight(dims, contentWidth, gap)
      : Math.max(...dims.map((d) => d.height));
  }

  return content + (padding.topLeft || 0) + (padding.bottomRight || 0);
}

function fluidSlideStoredHeight(slideWidget, breakpoint) {
  const h = pxToNumber(_.get(slideWidget, `properties.${breakpoint}.layout.height`));
  return h || 0;
}

const updateState = (widget) => {
  state.isFreeSize = _.get(widget, 'properties.desktop.free', false);
};
const updateBreakPoint = (breakpoint) => {
  state.breakPointType = breakpoint;
}

const CarouselWidgetView = function CarouselWidget({ className, style, widgetProps, widgetRef, children, inFlexFlow }) {
  const dispatch = useDispatch();
  const ref = widgetRef;

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const parent = _.get(widgetProps, 'parent');

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const shouldCloseManageSlides = useSelector(state => {
    const selected = _.get(state, `${editor}.present.selected`) || [];
    if (selected.length !== 1) return true;
    return selected[0] !== id;
  });

  const widgetSelected = useSelectedWidgetSelector(editor);
  const container = useContainerSelector(editor, id);

  const editorUIState = _.get(widgetProps, 'editorState');
  const stretched = _.get(widgetProps, 'properties.desktop.stretched', false);
  const freeSize = _.get(widgetProps, 'properties.desktop.free', false);
  const transition = _.get(widgetProps, 'properties.desktop.transition');
  const arrowsProperty = _.get(widgetProps, `properties.${breakpoint}.arrows`);
  const dotsProperty = _.get(widgetProps, `properties.${breakpoint}.dots`);

  const childrenWidgets = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`);
    return _.filter(widgets, (data) => { return data.parent === id });
  })

  const slideChildrenByParent = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`) || {};
    const slideIds = new Set(_.map(childrenWidgets, 'uid'));
    return _.groupBy(_.filter(widgets, (data) => slideIds.has(data.parent)), 'parent');
  })

  const childrenSelection = useChildrenWidgetSelected(editor, id);

  const background = _.get(widgetProps, 'properties.desktop.background');
  const defaultSlide = _.find(children, (child) => {
    return _.get(child, 'props.widgetProps.order') == 1;
  })
  const currentSlide = _.get(widgetProps, 'editorState.currentSlide');

  useEffect(() => {
    if (!_.some(childrenWidgets, { uid: currentSlide })) {
      const currentSlide = _.find(childrenWidgets, { order: 1 });
      dispatch(updateWidgetEditorState(editor, id, {
        currentSlide: currentSlide.uid
      }));
    }
  }, [])

  useEffect(()=>{
    if(widgetSelected){
    updateState(widgetSelected[0])
    }

    if(breakpoint){
      updateBreakPoint(breakpoint)
    }
  }, [widgetSelected, breakpoint])

  useEffect(() => {
    if (shouldCloseManageSlides) {
      dispatch(updateWidgetEditorState(editor, id, {
        manageSlides: false
      }));
    }
  }, [shouldCloseManageSlides])

  const carouselCardWidget = currentSlide ? _.find(children, (child) => { return _.get(child, 'props.widgetProps.uid') == currentSlide; }) : defaultSlide;
  const currentSlideOrder = _.get(carouselCardWidget, 'props.widgetProps.order');

  const currentSlideWidget = _.get(carouselCardWidget, 'props.widgetProps');
  const currentSlideFlexMode = _.get(getCardFlexConfig(currentSlideWidget, breakpoint), 'flexBoxEnabled', false);

  const anySlideFlex = _.some(childrenWidgets, (slide) => _.get(getCardFlexConfig(slide, breakpoint), 'flexBoxEnabled', false));
  const shownSlideEmpty = _.isEmpty(slideChildrenByParent[_.get(currentSlideWidget, 'uid')]);
  const flexModeActive = currentSlideFlexMode || (anySlideFlex && shownSlideEmpty);

  const fluidShownSlidePinned = anySlideFlex && !flexModeActive;

  const currentSlideId = _.get(currentSlideWidget, 'uid');
  const [measuredHeights, setMeasuredHeights] = useState({ desktop: {}, mobile: {} });
  useEffect(() => {
    setMeasuredHeights({ desktop: {}, mobile: {} });
  }, [id]);

  const slideSignatures = {};
  _.forEach(childrenWidgets, (slide) => {
    slideSignatures[slide.uid] = JSON.stringify([
      _.get(getCardFlexConfig(slide, breakpoint), 'flexProperty'),
      _.map(_.sortBy(slideChildrenByParent[slide.uid], 'uid'), (child) => [
        child.uid,
        _.get(child, `properties.${breakpoint}.layout.width`),
        _.get(child, `properties.${breakpoint}.layout.height`),
      ]),
    ]);
  });
  const prevSignaturesRef = useRef({ breakpoint: null, signatures: {} });
  useEffect(() => {
    const prev = prevSignaturesRef.current;
    prevSignaturesRef.current = { breakpoint, signatures: slideSignatures };
    if (prev.breakpoint !== breakpoint) return;
    const stale = _.union(_.keys(prev.signatures), _.keys(slideSignatures))
      .filter(uid => prev.signatures[uid] !== slideSignatures[uid]);
    if (!stale.length) return;
    setMeasuredHeights(prevHeights => {
      const nextDesktop = _.omit(prevHeights.desktop, stale);
      const nextMobile = _.omit(prevHeights.mobile, stale);
      if (_.size(nextDesktop) === _.size(prevHeights.desktop) && _.size(nextMobile) === _.size(prevHeights.mobile)) {
        return prevHeights;
      }
      return { desktop: nextDesktop, mobile: nextMobile };
    });
  });

  const liveBreakpointRef = useRef(breakpoint);
  liveBreakpointRef.current = breakpoint;

  const observeSlideInner = React.useCallback((slideId) => {
    if (!slideId) return undefined;
    const slideEl = document.getElementById(slideId);
    const inner = slideEl && slideEl.querySelector('[data-card-inner="true"]');
    if (!inner) return undefined;
    const slideWidget = _.find(childrenWidgets, { uid: slideId });
    const { flexProperty } = getCardFlexConfig(slideWidget, breakpoint);
    const bottomPad = _.get(resolvePadding(flexProperty.padding), 'bottomRight') || 0;

    const measure = () => {
      if (liveBreakpointRef.current !== breakpoint) return;
      const kids = _.filter(inner.children, (el) => el.nodeType === 1);
      if (!kids.length) return;
      const maxBottom = Math.max(..._.map(kids, (el) => {
        const base = el.offsetParent === inner ? 0 : inner.offsetTop;
        return el.offsetTop + el.offsetHeight - base;
      }));
      const h = Math.max(0, Math.round(maxBottom + bottomPad));
      setMeasuredHeights(prev => {
        const bucket = prev[breakpoint] || {};
        if (bucket[slideId] === h) return prev;
        return { ...prev, [breakpoint]: { ...bucket, [slideId]: h } };
      });
    };

    const observer = new ResizeObserver(measure);
    observer.observe(inner);
    _.forEach(inner.children, (el) => { if (el.nodeType === 1) observer.observe(el); });
    return () => observer.disconnect();
  }, [childrenWidgets, breakpoint]);

  useEffect(() => {
    if (!flexModeActive) return;
    return observeSlideInner(currentSlideId);
  }, [currentSlideId, flexModeActive, breakpoint, slideSignatures[currentSlideId], observeSlideInner]);

  const [warmUpSlideId, setWarmUpSlideId] = useState(null);

  // Slides that still need a real measurement at the current breakpoint.
  const slidesNeedingMeasure = React.useMemo(() => {
    if (!flexModeActive) return [];
    const bucket = measuredHeights[breakpoint] || {};
    return _.chain(childrenWidgets)
      .filter((slide) => {
        if (slide.uid === currentSlideId) return false; // visible slide is measured live
        if (!_.get(getCardFlexConfig(slide, breakpoint), 'flexBoxEnabled', false)) return false;
        if (_.isEmpty(slideChildrenByParent[slide.uid])) return false; // empty -> floor, no measure needed
        return bucket[slide.uid] == null;
      })
      .map('uid')
      .value();
  }, [flexModeActive, childrenWidgets, measuredHeights, breakpoint, currentSlideId, slideChildrenByParent]);

  useEffect(() => {
    if (!flexModeActive) { if (warmUpSlideId) setWarmUpSlideId(null); return; }
    const next = slidesNeedingMeasure[0] || null;
    if (next !== warmUpSlideId) setWarmUpSlideId(next);
  }, [flexModeActive, slidesNeedingMeasure, warmUpSlideId]);

  useEffect(() => {
    if (!flexModeActive || !warmUpSlideId) return undefined;
    let cleanup;
    let raf;
    let attempts = 0;
    const tryObserve = () => {
      cleanup = observeSlideInner(warmUpSlideId);
      if (!cleanup && attempts < 10) {
        attempts += 1;
        raf = requestAnimationFrame(tryObserve);
      }
    };
    raf = requestAnimationFrame(tryObserve);
    return () => { cancelAnimationFrame(raf); if (cleanup) cleanup(); };
  }, [flexModeActive, warmUpSlideId, breakpoint, observeSlideInner]);

  const warmUpSlideElement = warmUpSlideId
    ? _.find(children, (child) => _.get(child, 'props.widgetProps.uid') === warmUpSlideId)
    : null;

  const flexMinHeight = React.useMemo(() => {
    if (!anySlideFlex) return 0;
    const hasAnyContent = _.some(childrenWidgets, (slide) => !_.isEmpty(slideChildrenByParent[slide.uid]));
    const heights = _.map(childrenWidgets, (slide) => {
      const slideChildren = slideChildrenByParent[slide.uid];
      if (!slideChildren || slideChildren.length === 0) {
        return hasAnyContent ? 0 : CAROUSEL_EMPTY_FLOOR;
      }
      const slideFlex = _.get(getCardFlexConfig(slide, breakpoint), 'flexBoxEnabled', false);
      if (!slideFlex) {
        const stored = fluidSlideStoredHeight(slide, breakpoint);
        return stored > 0 ? stored : CAROUSEL_EMPTY_FLOOR;
      }
      const carouselEl = typeof document !== 'undefined' ? document.getElementById(id) : null;
      const estimated = estimateSlideContentHeight(slide, slideChildren, breakpoint, carouselEl);
      const measured = (measuredHeights[breakpoint] || {})[slide.uid];
      return measured != null
        ? measured
        : (estimated > 0 ? estimated : CAROUSEL_EMPTY_FLOOR);
    });
    return heights.length ? Math.max(...heights) : CAROUSEL_EMPTY_FLOOR;
  }, [anySlideFlex, childrenWidgets, measuredHeights, slideChildrenByParent, breakpoint, id]);

  useEffect(() => {
    if (breakpoint == 'mobile') {
      dispatch(updateWidgetEditorState(editor, id, {
        manageSlides: false
      }));
    }
  }, [breakpoint])

  var carouselWidgetStyle = {
    ...style,
    ...getStyles(widgetProps, breakpoint, container, inFlexFlow),
    ...(flexModeActive
      ? {
        height: 'auto',
        minHeight: `${flexMinHeight}px`,
        '--carousel-flex-min-height': `${flexMinHeight}px`,
      }
      : {}),
    ...(fluidShownSlidePinned && flexMinHeight > 0
      ? {
        minHeight: `${flexMinHeight}px`,
        '--carousel-flex-min-height': `${flexMinHeight}px`,
      }
      : {}),
  }

  var leftnavigationStyles = getNavigationButtonStyles('left', arrowsProperty, 'regular');
  var rightnavigationStyles = getNavigationButtonStyles('right', arrowsProperty, 'regular');

  return (
    <>
      <div
        id={id}
        ref={ref}
        className={cx(className, styles.carouselContainer, 'widget', {
          [styles.stretched]: stretched && !freeSize,
          [styles.flowed]: inFlexFlow,
          [styles.mobile]: breakpoint === 'mobile',
          [styles.partialSelection]: childrenSelection.selected,
        })}
        style={carouselWidgetStyle}
        data-widget-id={id}
        data-widget-type={type}
        data-selectable={true}
      >
        <BackgroundView background={background} />
        {carouselCardWidget &&
          (currentSlide || defaultSlide) && React.cloneElement(carouselCardWidget, {})
        }
        {warmUpSlideElement &&
          <div
            aria-hidden={true}
            data-carousel-measure-slot={true}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              visibility: 'hidden',
              pointerEvents: 'none',
              zIndex: -1,
              '--carousel-flex-min-height': 'auto',
            }}
          >
            <MeasurementContext.Provider value={true}>
              {React.cloneElement(warmUpSlideElement, {})}
            </MeasurementContext.Provider>
          </div>
        }
        {_.get(arrowsProperty, 'enabled') &&
          <>
            {(_.get(transition, 'autoPlay') || currentSlideOrder !== 1) &&
              <Button variant='info' style={leftnavigationStyles} className={styles.navigationButton}>
                {renderArrowIcons(_.get(arrowsProperty, 'style'), 'left', arrowsProperty)}
              </Button>
            }
            {(_.get(transition, 'autoPlay') || currentSlideOrder !== _.size(children)) &&
              <Button variant='info' style={rightnavigationStyles} className={styles.navigationButton}>
                {renderArrowIcons(_.get(arrowsProperty, 'style'), 'right', arrowsProperty)}
              </Button>
            }
          </>
        }
        {_.get(dotsProperty, 'flag') &&
          <div style={{ bottom: `${_.get(dotsProperty, 'offset')}px` }} className={styles.paginationRowContainer}>
            {
              _.map(_.orderBy(childrenWidgets, ['order'], ['asc']), (data, index) => {
                var isActive = _.get(data, 'uid') == currentSlide;
                var lastIndex = _.size(childrenWidgets) - 1
                return renderDotsIcons(dotsProperty, index, isActive, lastIndex)
              })
            }
          </div>
        }
      </div>

      {_.get(editorUIState, 'manageSlides') &&
        <ManageSlidesDialog
          id={id}
          parent={id}
          topParent={parent}
          editor={editor}
          widget={widgetProps}
          children={children}
          childrenWidgets={childrenWidgets}
          onCancel={() => {
            dispatch(updateWidgetEditorState(editor, id, {
              manageSlides: false
            }));
          }}
        />
      }
    </>
  );
};

const CarouselWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const store = useStore();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const storyboardContext = React.useContext(StoryboardContext);

  const storyboardType = _.get(storyboardContext, 'type');
  const id = _.get(props, 'uid');
  const type = _.get(props, 'type');
  const parent = _.get(props, 'parent');
  const container = useContainerSelector(editorSession, id);
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const childrenWidgets = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`);
    return _.filter(widgets, (data) => { return data.parent === id });
  })
  const currentSlide = _.get(props, 'editorState.currentSlide');
  const currentSlideWidget = (Array.isArray(childrenWidgets)
    ? childrenWidgets.find(widget => widget?.uid === currentSlide)
    : null) || _.find(childrenWidgets, { order: 1 });
  const isCurrentSlideLocked = _.get(currentSlideWidget, `properties.${breakpoint}.locked`);
  const currentSlideFlexMode = _.get(getCardFlexConfig(currentSlideWidget, breakpoint), 'flexBoxEnabled', false);

  const background = getCarouselBackground(childrenWidgets, currentSlide);

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });
  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const entityType = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`));
  const allowBrandkit = entityType !== 'PALETTE_COMPONENT';

  const freeSize = _.get(props, 'properties.desktop.free', false);
  const { makeFrameShow, makeFramePageId } = useMakeFrame({ editorSession });

  useEffect(() => {
    if (!currentSlide) {
      handleSlideChange(1)
    }
  }, [])

  useEffect(() => {
    setView(null);
  },[id])

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);

    dispatch(updateWidgetEditorState(editorSession, id, {
      manageSlides: false
    }));
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  const handleManageSlides = () => {
    dispatch(toggleEditMode(editorSession, props.uid, true));
    setView(null);

    dispatch(updateWidgetEditorState(editorSession, id, {
      manageSlides: true
    }));
  }

  const handleSlideChange = (curentIndex) => {
    const currentSlide = _.find(childrenWidgets, { order: curentIndex });
    dispatch(updateWidgetEditorState(editorSession, id, {
      currentSlide: currentSlide.uid
    }));
  }
  
  const handleMakeFrame = () => {
    dispatch(splitPageBySelection(editorSession, makeFramePageId, [id]));
  };

  const handleFlexModeToggle = () => {
    if (!currentSlideWidget) return;
    toggleCardFlexMode(dispatch, store, editorSession, currentSlideWidget.uid, currentSlideWidget, breakpoint, currentSlideFlexMode);
    resyncMoveableTargets(editorContext.editorSession);
  };

  return (
    <>
      <Property.SlideChanger slides={childrenWidgets} parent={id} currentSlideId={currentSlide} onChange={handleSlideChange} editorSession={editorSession} type={type} topParent={parent} />
      <Property.Divider />
      <Property.Action className={styles.textButton} onClick={handleManageSlides}>
        Manage Slides
      </Property.Action>
      <Property.Divider />
      {!isCurrentSlideLocked &&
        <Property.Action className={styles.textButton} onClick={() => changeView('background')}>
          Change Background
        </Property.Action>
      }
      {!isCurrentSlideLocked && <Property.Divider />}

      {!isCurrentSlideLocked && currentSlideWidget && isFlexModeAllowed(storyboardType, entityType) &&
        <>
          <Property.Action className={styles.textButton} onClick={() => changeView('layout')}>
            Layout
          </Property.Action>
          <Property.Divider />
        </>
      }

      {!isCurrentSlideLocked && makeFrameShow && !freeSize &&
        <>
          <Property.Action className={styles.textButton} tooltip='Split segment: selection becomes a new frame' onClick={handleMakeFrame}>
            Make Frame
          </Property.Action>
          <Property.Divider />
        </>
      }

      <Property.Action className={styles.iconButton} tooltip="Settings" onClick={() => changeView('settings')}>
        <i className={cx("cleverstory-settings", styles.settingsIcon)} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip="Arrows and Dots" onClick={() => changeView('style')}>
        <i className={cx("cleverstory-customize", styles.customizeIcon)} />
      </Property.Action>
      {(_.get(container, 'type') === PAGE || _.get(container, 'type') === SLIDE) &&
        <Property.Action className={styles.iconButton} tooltip='Stretch' onClick={() => changeView('stretch')}>
          <i className={cx("cleverstory-stretch")} />
        </Property.Action>
      }
      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {!editMode && view === 'background' &&
        <Background.Dialog id={id}
          value={background}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, currentSlide, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {!editMode && view === 'settings' &&
        <CarouselSettingsEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'style' &&
        <CarouselStyleEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'stretch' &&
        <StretchCarouselDialog
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
          storyboardType={storyboardType}
        />
      }

      {!editMode && view === 'layout' && currentSlideWidget &&
        <LayoutConfigPopup
          id={id}
          flexMode={currentSlideFlexMode}
          onToggle={handleFlexModeToggle}
          onCancel={() => closePopup()}
          renderFlexControls={() => (
            <CarouselFlexControls
              slideId={currentSlideWidget.uid}
              slideWidget={currentSlideWidget}
              breakpoint={breakpoint}
            />
          )}
        />
      }

      {view === 'brandMedia' &&
        <BrandComponentProperty type={'CAROUSELS'} widgets={[props]} onCancel={() => closePopup()} />
      }

    </>
  );
}

function computeFluidMarginLeft(slideWidget, breakpoint) {
  const free = _.get(slideWidget, 'properties.desktop.free', false);
  if (free) return '0px';

  const container = _.get(slideWidget, `properties.${breakpoint}.container`);
  const minWidth = _.get(container, 'minWidth');
  if (!minWidth) return '0px';

  const alignment = _.get(container, 'alignment.position') || _.get(container, 'alignment', 'center');
  let multiplier = 0.5;
  if (alignment === 'left') multiplier = 0;
  else if (alignment === 'right') multiplier = 1;

  return `calc((100% - ${minWidth}px) * ${multiplier})`;
}

function transformWithZeroTranslate(layout) {
  const t = _.get(layout, 'transform') || {};
  const rotate = t.rotate ? ` rotate(${t.rotate})` : '';
  const scale = t.scale ? ` scale(${t.scale})` : '';
  return `translate(0px, 0px)${rotate}${scale}`;
}

function pinFluidPositionsFromFlex(dispatch, editor, slideId, slideWidget, breakpoint, childWidgets, slideFlexProps, carouselWidget) {
  const cardEl = document.getElementById(slideId);
  const inner = cardEl && cardEl.querySelector('[data-card-inner="true"]');
  if (!inner) return false;

  const items = Array.from(inner.querySelectorAll(':scope > [data-widget-type]'));
  if (items.length === 0) return false;

  const byUid = _.keyBy(childWidgets, 'uid');
  if (inner.offsetWidth === 0) return false;
  const scale = (inner.getBoundingClientRect().width / inner.offsetWidth) || 1;

  const isFree = _.get(carouselWidget, 'properties.desktop.free', false) && breakpoint === 'desktop';
  const carouselEl = carouselWidget && document.getElementById(_.get(carouselWidget, 'uid'));
  const flexContentWidth = isFree && carouselEl
    ? Math.round((carouselEl.getBoundingClientRect().width / scale) * 100) / 100
    : null;
  const flexContentHeight = carouselEl
    ? Math.round((carouselEl.getBoundingClientRect().height / scale) * 100) / 100
    : null;

  const savedInnerTransition = inner.style.transition;
  inner.style.transition = 'none';
  const savedItemTransition = items.map(el => el.style.transition);
  items.forEach(el => { el.style.transition = 'none'; });

  const innerRect1 = inner.getBoundingClientRect();
  const flexRects = items.map(el => {
    const r = el.getBoundingClientRect();
    return { left: (r.left - innerRect1.left) / scale, top: (r.top - innerRect1.top) / scale };
  });

  const savedInner = {
    display: inner.style.display, flexDirection: inner.style.flexDirection,
    justifyContent: inner.style.justifyContent, alignItems: inner.style.alignItems,
    alignContent: inner.style.alignContent,
    flexWrap: inner.style.flexWrap, columnGap: inner.style.columnGap, rowGap: inner.style.rowGap,
    padding: inner.style.padding, boxSizing: inner.style.boxSizing,
    width: inner.style.width, height: inner.style.height,
  };
  inner.style.width = `${innerRect1.width / scale}px`;
  inner.style.height = `${innerRect1.height / scale}px`;
  inner.style.display = 'block';
  inner.style.flexDirection = '';
  inner.style.justifyContent = '';
  inner.style.alignItems = '';
  inner.style.alignContent = '';
  inner.style.flexWrap = '';
  inner.style.columnGap = '';
  inner.style.rowGap = '';
  inner.style.padding = '';
  inner.style.boxSizing = '';

  const fluidMargin = computeFluidMarginLeft(slideWidget, breakpoint);
  const savedItem = items.map(el => ({
    position: el.style.position, marginLeft: el.style.marginLeft, transform: el.style.transform,
    width: el.style.width, height: el.style.height,
    maxWidth: el.style.maxWidth, flexShrink: el.style.flexShrink,
  }));
  items.forEach(el => {
    const uid = el.getAttribute('data-widget-id') || el.id;
    const layout = _.get(byUid[uid], `properties.${breakpoint}.layout`);
    el.style.position = 'absolute';
    el.style.marginLeft = fluidMargin;
    el.style.transform = transformWithZeroTranslate(layout);
    const w = _.get(layout, 'width');
    const h = _.get(layout, 'height');
    el.style.width = w == null ? '' : (typeof w === 'number' ? `${w}px` : w);
    el.style.height = h == null ? '' : (typeof h === 'number' ? `${h}px` : h);
    el.style.maxWidth = '';
    el.style.flexShrink = '';
  });

  void inner.offsetWidth; // force reflow before the second measure

  const innerRect2 = inner.getBoundingClientRect();
  const staticRects = items.map(el => {
    const r = el.getBoundingClientRect();
    return { left: (r.left - innerRect2.left) / scale, top: (r.top - innerRect2.top) / scale };
  });

  const updates = items.map((el, i) => {
    const uid = el.getAttribute('data-widget-id') || el.id;
    const tx = isFree
      ? Math.round(flexRects[i].left * 100) / 100
      : Math.round((flexRects[i].left - staticRects[i].left) * 100) / 100;
    const ty = isFree
      ? Math.round(flexRects[i].top * 100) / 100
      : Math.round((flexRects[i].top - staticRects[i].top) * 100) / 100;
    return { uid, tx, ty };
  });

  items.forEach((el, i) => {
    el.style.position = savedItem[i].position;
    el.style.marginLeft = savedItem[i].marginLeft;
    el.style.transform = savedItem[i].transform;
    el.style.width = savedItem[i].width;
    el.style.height = savedItem[i].height;
    el.style.maxWidth = savedItem[i].maxWidth;
    el.style.flexShrink = savedItem[i].flexShrink;
    el.style.transition = savedItemTransition[i];
  });
  Object.assign(inner.style, savedInner);
  inner.style.transition = savedInnerTransition;

  actionGroup.start();
  dispatch(updateWidgetProperty(editor, slideId, breakpoint, slideFlexProps || { flexBoxEnabled: false }));
  updates.forEach(({ uid, tx, ty }) => {
    const layout = _.get(byUid[uid], `properties.${breakpoint}.layout`);
    if (!layout) return;
    const frame = new Frame(layout);
    frame.remove('transform', 'translateX');
    frame.remove('transform', 'translateY');
    frame.set('transform', 'translate', `${tx}px, ${ty}px`);
    dispatch(updateWidgetProperty(editor, uid, breakpoint, { layout: frame.get() }));
  });
  if (carouselWidget && (flexContentWidth || flexContentHeight)) {
    const carouselLayout = _.get(carouselWidget, `properties.${breakpoint}.layout`);
    if (carouselLayout) {
      const cFrame = new Frame(carouselLayout);
      if (flexContentWidth) {
        const storedW = pxToNumber(_.get(carouselLayout, 'width')); // null/undefined -> 0
        cFrame.set('width', `${Math.max(storedW, flexContentWidth)}px`);
      }
      if (flexContentHeight) {
        cFrame.set('height', `${flexContentHeight}px`);
      }
      dispatch(updateWidgetProperty(editor, _.get(carouselWidget, 'uid'), breakpoint, { layout: cFrame.get() }));
    }
  }
  actionGroup.end();

  return true;
}

function hugChildrenBottomHeightOnFlexOn(slideId, breakpoint, carouselWidget) {
  const carouselEl = carouselWidget && document.getElementById(_.get(carouselWidget, 'uid'));
  const cardEl = document.getElementById(slideId);
  const inner = cardEl && cardEl.querySelector('[data-card-inner="true"]');
  if (!carouselEl || !inner) return null;
  if (inner.offsetWidth === 0) return null; // no layout box (hidden / mid-transition)

  const items = Array.from(inner.querySelectorAll(':scope > [data-widget-type]'));
  if (items.length === 0) return null;

  const scale = (inner.getBoundingClientRect().width / inner.offsetWidth) || 1;

  const carouselTop = carouselEl.getBoundingClientRect().top;
  const childrenEnd = items.reduce((maxBottom, el) => {
    const bottom = (el.getBoundingClientRect().bottom - carouselTop) / scale;
    return Math.max(maxBottom, bottom);
  }, 0);

  const storedHeight = pxToNumber(_.get(carouselWidget, `properties.${breakpoint}.layout.height`));

  if (storedHeight >= childrenEnd) return null;
  return Math.round(childrenEnd * 100) / 100;
}

function pinCarouselFlexHeight(carouselWidget, breakpoint) {
  if (!carouselWidget) return null;
  const carouselEl = document.getElementById(_.get(carouselWidget, 'uid'));
  if (!carouselEl) return null;
  const rect = carouselEl.getBoundingClientRect();
  if (rect.height === 0) return null; // no layout box (hidden / mid-transition)

  const scale = (rect.height / carouselEl.offsetHeight) || 1;
  const measuredHeight = Math.round((rect.height / scale) * 100) / 100;

  const carouselLayout = _.get(carouselWidget, `properties.${breakpoint}.layout`);
  if (!carouselLayout) return null;

  const cFrame = new Frame(carouselLayout);
  cFrame.set('height', `${measuredHeight}px`);
  return cFrame.get();
}

function toggleCardFlexMode(dispatch, store, editor, slideId, slideWidget, breakpoint, flexBoxEnabled) {
  const overrideMarker = breakpoint === 'mobile' ? { flexModeOverridden: true } : {};

  if (!flexBoxEnabled) {
    const widgets = _.get(store.getState(), `${editor}.present.widgets`, []) || [];
    const carouselWidget = widgets.find(w => w.uid === _.get(slideWidget, 'parent'));
    const huggedHeight = hugChildrenBottomHeightOnFlexOn(slideId, breakpoint, carouselWidget);

    actionGroup.start();
    dispatch(updateWidgetProperty(editor, slideId, breakpoint, { flexBoxEnabled: true, ...overrideMarker }));
    if (huggedHeight != null && carouselWidget) {
      const carouselLayout = _.get(carouselWidget, `properties.${breakpoint}.layout`);
      if (carouselLayout) {
        const cFrame = new Frame(carouselLayout);
        cFrame.set('height', `${huggedHeight}px`);
        dispatch(updateWidgetProperty(editor, _.get(carouselWidget, 'uid'), breakpoint, { layout: cFrame.get() }));
      }
    }
    actionGroup.end();
    return;
  }

  const widgets = _.get(store.getState(), `${editor}.present.widgets`, []) || [];
  const childWidgets = widgets.filter(w => w.parent === slideId);
  const carouselWidget = widgets.find(w => w.uid === _.get(slideWidget, 'parent'));
  const slideFlexProps = { flexBoxEnabled: false, ...overrideMarker };

  if (childWidgets.length === 0) {
    const pinnedLayout = pinCarouselFlexHeight(carouselWidget, breakpoint);
    actionGroup.start();
    dispatch(updateWidgetProperty(editor, slideId, breakpoint, slideFlexProps));
    if (pinnedLayout && carouselWidget) {
      dispatch(updateWidgetProperty(editor, _.get(carouselWidget, 'uid'), breakpoint, { layout: pinnedLayout }));
    }
    actionGroup.end();
    return;
  }

  const pinned = pinFluidPositionsFromFlex(dispatch, editor, slideId, slideWidget, breakpoint, childWidgets, slideFlexProps, carouselWidget);
  if (!pinned) {
    requestAnimationFrame(() => {
      const retried = pinFluidPositionsFromFlex(dispatch, editor, slideId, slideWidget, breakpoint, childWidgets, slideFlexProps, carouselWidget);
      if (!retried) {
        const pinnedLayout = pinCarouselFlexHeight(carouselWidget, breakpoint);
        actionGroup.start();
        dispatch(updateWidgetProperty(editor, slideId, breakpoint, slideFlexProps));
        if (pinnedLayout && carouselWidget) {
          dispatch(updateWidgetProperty(editor, _.get(carouselWidget, 'uid'), breakpoint, { layout: pinnedLayout }));
        }
        actionGroup.end();
      }
    });
    return;
  }
}

const resolveCardFlexLayout = (slideWidget, breakpoint) =>
  getCardFlexConfig(slideWidget, breakpoint).flexProperty;

const CarouselFlexControls = ({ slideId, slideWidget, breakpoint }) => {
  const { flexBoxEnabled } = getCardFlexConfig(slideWidget, breakpoint);
  return (
    <FlexConfigControls
      id={slideId}
      widget={slideWidget}
      resolveLayout={resolveCardFlexLayout}
      disabled={!flexBoxEnabled}
    />
  );
}

const CarouselFlexConfigDialog = ({ carouselId, slideId, slideWidget, breakpoint, onCancel }) => {
  useFlexConfigDismissOnClickOutside(onCancel, styles.flexConfigDialog);

  return (
    <Property.Dialog className={styles.flexConfigDialog}
      width={280}
      target={`#${carouselId}`}
      title={'Flex Layout'}
      onCancel={onCancel}
    >
      <Property.Body>
        <CarouselFlexControls slideId={slideId} slideWidget={slideWidget} breakpoint={breakpoint} />
      </Property.Body>
    </Property.Dialog>
  );
}

const CarouselWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const storyboardType = _.get(React.useContext(StoryboardContext), 'type');
  const parent = _.get(props, 'parent');
  const [view, setView] = useState(null);
  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const entityType = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`));
  const allowBrandkit = entityType !== 'PALETTE_COMPONENT';

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const id = _.get(props, 'uid');
  const type = _.get(props, 'type');
  const currentSlide = _.get(props, 'editorState.currentSlide');
  const childrenWidgets = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`);
    return _.filter(widgets, (data) => { return data.parent === id });
  })

  const currentSlideWidget = (Array.isArray(childrenWidgets)
    ? childrenWidgets.find(w => w?.uid === currentSlide)
    : null) || _.find(childrenWidgets, { order: 1 });
  const isCurrentSlideLocked = _.get(currentSlideWidget, `properties.${breakpoint}.locked`);
  const desktopSlideFlexMode = _.get(getCardFlexConfig(currentSlideWidget, 'desktop'), 'flexBoxEnabled', false);

  const changeView = (view) => {
    setView(view);
  }

  const closePopup = () => {
    setView(null);
  }

  const handleSlideChange = (curentIndex) => {
    const currentSlide = _.find(childrenWidgets, { order: curentIndex });
    dispatch(updateWidgetEditorState(editorSession, id, {
      currentSlide: currentSlide.uid
    }));
  }


  return (
    <>
      <Property.SlideChanger slides={childrenWidgets} parent={id} currentSlideId={currentSlide} onChange={handleSlideChange} editorSession={editorSession} type={type} topParent={parent}/>
      <Property.Divider />
      <Property.Action className={styles.iconButton} tooltip='Arrows and Dots' onClick={() => changeView('design')}>
        <i className={cx("cleverstory-customize")} />
      </Property.Action>
      {!isCurrentSlideLocked && currentSlideWidget && isFlexModeAllowed(storyboardType, entityType) && desktopSlideFlexMode &&
        <>
          <Property.Action className={styles.textButton} tooltip='Layout' onClick={() => changeView('layout')}>
            Layout
          </Property.Action>
          <Property.Divider />
        </>
      }
      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {view === 'design' &&
        <CarouselStyleEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
      {view === 'layout' && currentSlideWidget &&
        <CarouselFlexConfigDialog
          carouselId={id}
          slideId={currentSlideWidget.uid}
          slideWidget={currentSlideWidget}
          breakpoint={breakpoint}
          onCancel={() => closePopup()}
        />
      }
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'CAROUSELS'} widgets={[props]} onCancel={() => closePopup()} />
      }
    </>
  );
}

const CarouselStyleEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const properties = _.get(widget, `properties.${breakpoint}`, {});
  const arrowProperty = _.get(properties, `arrows`, {}) || {};
  const dotsProperty = _.get(properties, `dots`, {}) || {};

  const [state, setState] = useState({
    activeTab: 'regular'
  })

  const handleTabChange = (key) => {
    setState(() => {
      return {
        ...state,
        activeTab: key
      }
    })
  }

  const handleArrowPropertyChange = (field, value) => {
    dispatch(updateWidgetProperty(editorSession, widget.uid, breakpoint, {
      arrows: {
        ...arrowProperty,
        [field]: value
      }
    }));
  }

  const handleArrowColorChange = React.useCallback((color) => {
    var request = {
      ...arrowProperty[state.activeTab],
      color: color
    }

    handleArrowPropertyChange(state.activeTab, request);
  }, [properties, state.activeTab]);

  const handleArrowBackgroundColorChange = React.useCallback((color) => {
    var request = {
      ...arrowProperty[state.activeTab],
      backgroundColor: color
    }

    handleArrowPropertyChange(state.activeTab, request);
  }, [properties, state.activeTab]);

  const handleDotsPropertyChange = (field, value) => {
    dispatch(updateWidgetProperty(editorSession, widget.uid, breakpoint, {
      dots: {
        ...dotsProperty,
        [field]: value
      }
    }));
  }

  return (
    <Property.Dialog className={styles.styleDialog}
      width={250}
      target={`#${id}`}
      title={'Arrows & Dots'}
      onCancel={rest.onCancel}
    >
      <Property.Body className={styles.container}>
        <Property className={styles.arrowSettings}>
          <Property.Switch
            id="arrows"
            title={'Arrows'}
            className={cx(styles.enableSwitch, { [styles.activated]: arrowProperty.enabled })}
            value={arrowProperty.enabled}
            onChange={() => handleArrowPropertyChange('enabled', !arrowProperty.enabled)}
          />
          {arrowProperty.enabled &&
            <>
              <Property name="Arrow Styles" className={styles.arrowStyles}>
                <div className={styles.buttonConatiner}>
                  {CAROUSEL_ARROWS.map((data, index) => {
                    return (
                      <div key={index} onClick={() => { handleArrowPropertyChange('style', data.type) }} className={cx(styles.arrowButton, { [styles.active]: data.type === _.get(arrowProperty, 'style') })}>
                        {renderArrowIcons(data.type, 'left')}
                        {renderArrowIcons(data.type, 'right')}
                      </div>
                    )
                  })
                  }
                </div>
              </Property>
              <Property name="Arrow Size" className={styles.arrowSize}>
                <Property.Slider
                  input
                  min={8}
                  max={72}
                  step={1}
                  value={_.get(arrowProperty, 'size')}
                  onChange={(value) => { handleArrowPropertyChange('size', value) }}
                />
              </Property>
              <Property name="Offset" className={styles.arrowOffset}>
                <Property.Slider
                  input
                  min={0}
                  max={72}
                  step={1}
                  value={_.get(arrowProperty, 'offset')}
                  onChange={(value) => { handleArrowPropertyChange('offset', value) }}
                />
              </Property>
              <Property name="Background Corner Radius" className={styles.arrowRadius}>
                <Property.Slider
                  input
                  min={0}
                  max={50}
                  step={1}
                  value={_.get(arrowProperty, 'backgroundCornerRadius')}
                  onChange={(value) => { handleArrowPropertyChange('backgroundCornerRadius', value) }}
                />
              </Property>
            </>
          }
        </Property>
        {arrowProperty.enabled &&
          <Property className={styles.buttonDesignProperty}>
            <Tabs
              activeKey={state.activeTab}
              onSelect={handleTabChange}
              className={styles.buttonStyleTabs}>
              <Tab eventKey="regular" title="Regular" className={styles.buttonStyleTab}>
                <Property.Color title={'Arrow Colour'} value={_.get(arrowProperty, 'regular.color')} onChange={(color) => handleArrowColorChange(color, 'regular')} />
                <Property.Color title={'Background Colour'} clearable={true} value={_.get(arrowProperty, 'regular.backgroundColor')} onChange={handleArrowBackgroundColorChange} />
              </Tab>
              <Tab eventKey="hover" title="Hover" className={styles.buttonStyleTab}>
                <Property.Color title={'Arrow Colour'} value={_.get(arrowProperty, 'hover.color')} onChange={handleArrowColorChange} />
                <Property.Color title={'Background Colour'} clearable={true} value={_.get(arrowProperty, 'hover.backgroundColor')} onChange={handleArrowBackgroundColorChange} />
              </Tab>
            </Tabs>
          </Property>
        }
        <Property className={styles.dotSettings}>
          <Property.Switch
            id="dots"
            title={'Pagination Dots'}
            value={dotsProperty.flag}
            className={cx(styles.enableSwitch, { [styles.activated]: dotsProperty.flag })}
            onChange={() => handleDotsPropertyChange('flag', !dotsProperty.flag)}
          />
          {dotsProperty.flag &&
            <>
              <Property name="Dots Styles" className={styles.dotsStyles}>
                <div className={styles.buttonConatiner}>
                  {CAROUSEL_DOTS.map((data, index) => {
                    return (
                      <div key={index} onClick={() => { handleDotsPropertyChange('style', data.type) }} className={cx(styles.arrowButton, { [styles.active]: data.type === _.get(dotsProperty, 'style') })}>
                        {_.times(3, (dotIndex) => {
                          return renderDotsIcons(data, dotIndex)
                        })}
                      </div>
                    )
                  })
                  }
                </div>
              </Property>
              <Property.Color className={styles.colorProperty} title={'Default Colour'} value={_.get(dotsProperty, 'defaultColor')} onChange={(color) => { handleDotsPropertyChange('defaultColor', color) }} />
              <Property.Color className={styles.colorProperty} title={'Selected Colour'} value={_.get(dotsProperty, 'selectedColor')} onChange={(color) => { handleDotsPropertyChange('selectedColor', color) }} />
              <Property name="Dots Size" className={styles.dotsSize}>
                <Property.Slider
                  input
                  min={8}
                  max={24}
                  step={1}
                  value={_.get(dotsProperty, 'size')}
                  onChange={(value) => { handleDotsPropertyChange('size', value) }}
                />
              </Property>
              <Property name="Space Between Dots" className={styles.dotsSpace}>
                <Property.Slider
                  input
                  min={5}
                  max={30}
                  step={1}
                  value={_.get(dotsProperty, 'cornerRadius', 4)}
                  onChange={(value) => { handleDotsPropertyChange('cornerRadius', value) }}
                />
              </Property>
              <Property name="Offset" className={styles.dotsOffset}>
                <Property.Slider
                  input
                  min={0}
                  max={72}
                  step={1}
                  value={_.get(dotsProperty, 'offset')}
                  onChange={(value) => { handleDotsPropertyChange('offset', value) }}
                />
              </Property>
            </>
          }
        </Property>
      </Property.Body>
    </Property.Dialog>
  )
}

const CarouselSettingsEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const transition = _.get(widget, `properties.${breakpoint}.transition`, {}) || {};

  const handlePropertyChange = (field, value) => {
    dispatch(updateWidgetProperty(editorSession, widget.uid, breakpoint, {
      transition: {
        ...transition,
        [field]: value
      }
    }));
  }

  return (
    <Property.Dialog className={styles.settingsDialog}
      width={250}
      target={`#${id}`}
      title={'Transition Settings'}
      onCancel={rest.onCancel}
    >
      <Property.Body className={styles.dialogBody}>
        <Property className={styles.autoPlaySettings}>
          <Property.Switch
            id="autoPlay"
            title={'Autoplay & Loop'}
            value={transition.autoPlay}
            onChange={() => handlePropertyChange('autoPlay', !transition.autoPlay)}
          />
          {transition.autoPlay &&
            <div className={styles.configurations}>
              <Property name="Slide Visible Duration" className={styles.durationProperty}>
                <Property.Slider
                  input
                  min={1}
                  max={5}
                  step={1}
                  value={_.get(transition, 'visibleDuration')}
                  onChange={(value) => { handlePropertyChange('visibleDuration', value) }}
                />
              </Property>
              <Property.Switch
                id="pauseOnHover"
                title={'Pause On Hover'}
                value={transition.pauseOnHover}
                onChange={() => handlePropertyChange('pauseOnHover', !transition.pauseOnHover)}
              />
            </div>
          }
        </Property>
        <Property name="Transition Effects" nameStyle={styles.propertyName} className={styles.transitionEffectsSettings}>
          <Property name="Transition Speed" className={styles.transtionSpeedProperty}>
            <Property.Slider
              input
              min={1}
              max={5}
              step={1}
              value={_.get(transition, 'speed')}
              onChange={(value) => { handlePropertyChange('speed', value) }}
            />
          </Property>
          <div className={styles.animationContainer}>
            <div className={styles.grid}>
              {
                CAROUSEL_TRANSITION.map((item, index) => {
                  const active = _.get(transition, 'style') === item.type;
                  return (
                    <div key={index}
                      className={cx(styles.transitionTypeButton, {
                        [styles.active]: active
                      })}
                      onClick={() => handlePropertyChange('style', item.type)}
                    >
                      <div className={styles.icon}>
                        <img src={item.buttonImg} />
                      </div>
                      <span>{item.name}</span>
                    </div>
                  );
                })
              }
            </div>
          </div>
        </Property>
      </Property.Body>
    </Property.Dialog>
  )
}

/**
 * Manage Slides Dialog
 * 
*/
const ManageSlidesDialog = ({ id, parent, widget, children, childrenWidgets, position, topParent, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const parentType = getWidgetType(editorSession, topParent);
  let currentSlide = _.get(widget, 'editorState.currentSlide');
  currentSlide = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    return widgets.find(item => item.uid === currentSlide);
  });
  const handleAddSlides = () => {
    const defaultChild = _.head(children);
    let newSlideProperties = _.get(defaultChild, 'props.widgetProps.properties');
    newSlideProperties = {
      ...newSlideProperties,
      "desktop": {
        ...newSlideProperties.desktop,
        "locked": false,
        "background": {
          "type": "color",
          "color": {
            "type": "solid",
            "r": 0,
            "g": 0,
            "b": 0,
            "a": 1
          },
          "media": null,
          "opacity": 0,
          "scale": null,
          "position": null
        },
        "container": {
          "minWidth": 1200 * 100 / 100,
          "alignment": "center"
        },
      },
      "mobile": {
        ...newSlideProperties.mobile,
        "locked": false 
      }
    }

    if (_.size(childrenWidgets) == 50) {
      return;
    }

    actionGroup.start();
    dispatch(createWidget(
      editorSession,
      [
        {
          type: CAROUSEL_CARD,
          name: `Slide`,
          properties: newSlideProperties
        },
      ],
      parent
    ))
    if (parentType !== HOTSPOT){
      dispatch(toggleHotspotView(editorSession, null)); 
    }
    dispatch(selectWidget(editorSession, [parent])) 
    actionGroup.end();
  }

  return (
    <Property.Dialog className={styles.manageSlidesDialog}
      width={250}
      target={`#${id}`}
      title={'Manage Slides'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property className={styles.listView}>
          <SliderView parent={id} currentSlide={currentSlide} slides={_.orderBy(childrenWidgets, ['order'], ['asc'])} editorSession={editorSession} topParent={topParent}/>
        </Property>
        <Property className={styles.actionContainer}>
          <Button variant='primary' onClick={handleAddSlides}>
            <i className={cx('cleverstory-plus', styles.plusIcon)} />
            Add New Slide
          </Button>
        </Property>
      </Property.Body>
    </Property.Dialog>
  )
}

const SliderView = ({ slides, parent, currentSlide, editorSession, topParent }) => {
  const dispatch = useDispatch();
  const parentType = getWidgetType(editorSession, topParent);

  const handleMove = (dragIndex, hoverIndex) => {
    const dragWidget = _.find(slides, { order: (dragIndex + 1) });
    const hoverWidget = _.find(slides, { order: (hoverIndex + 1) });
    if (parentType !== HOTSPOT){
      dispatch(toggleHotspotView(editorSession, null));
    }
    dispatch(arrangeCarouselSlides(editorSession, dragWidget, hoverIndex));

    if (_.isEqual(currentSlide, dragWidget)) {
      changeCurrentSlide(hoverWidget)
    }

    if (_.isEqual(currentSlide, hoverWidget)) {
      changeCurrentSlide(dragWidget)
    }
  }

  const changeCurrentSlide = (slide) => {
    if (slide) {
      dispatch(updateWidgetEditorState(editorSession, parent, {
        currentSlide: slide.uid
      }));
    }
  }

  const onDelete = (id) => {

    if (id == _.get(currentSlide, 'uid', null)) {
      const deleteWidget = _.find(slides, { 'uid': id });
      const order = _.get(deleteWidget, 'order') == _.size(slides) ? (_.get(deleteWidget, 'order') - 1) : (_.get(deleteWidget, 'order') + 1)
      const newCurrentSlide = _.find(slides, { 'order': order })
      dispatch(updateWidgetEditorState(editorSession, parent, {
        currentSlide: newCurrentSlide.uid
      }));
    }
  }

  return (
      <div className={styles.sortableSlidesList}>
        {slides && _.map(slides, (slide, index) => {
          return (
            <SliderItem id={slide.uid} sliderData={slide}
              moveSlide={handleMove}
              slides={slides}
              currentSlide={currentSlide}
              parent={parent}
              topParent={topParent}
              index={index}
              onDelete={onDelete}
              editorSession={editorSession} />
          )
        })
        }
      </div>
  )
}

const SliderItem = ({ id, sliderData, slides, parent, currentSlide, onDelete, moveSlide, index, editorSession, topParent }) => {
  const dispatch = useDispatch();
  const ref = React.useRef(null);
  const inputRef = React.useRef(null);
  const prevStateRef = React.useRef(slides);
  const [editable, setEditable] = useState(false);
  const [inputName, setInputName] = useState(_.get(sliderData, 'name'));
  const parentType = getWidgetType(editorSession, topParent);
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const allWidgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`)) || [];

  const isSlideLocked = isWidgetLocked(allWidgets, sliderData, breakpoint)

  const [{ handlerId }, drop] = useDrop({
    accept: 'SLIDE',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      // Time to actually perform the action
      moveSlide(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'SLIDE',
    item: () => {
      return { id, index }
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  useEffect(() => {
    const hasIncreased = slides.length > prevStateRef.current.length;
    const newSlide = _.find(slides, function (data) { return data.order == _.size(slides); })

    if (hasIncreased && newSlide?.uid && !_.isEqual(currentSlide.uid, newSlide.uid)) {
      dispatch(updateWidgetEditorState(editorSession, parent, {
        currentSlide: newSlide.uid
      }));
    }
    prevStateRef.current = slides;
  }, [slides])

  const handleClone = () => {
    if (sliderData) {
      if (parentType !== HOTSPOT){
        dispatch(toggleHotspotView(editorSession, null));
      }
      dispatch(cloneWidgets(editorSession, _.get(sliderData, "parent"), [_.get(sliderData, "uid")]))
    }
  }

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  useEffect(() => {
    if (editable) {
      inputRef.current.focus();
    }
  }, [editable]);

  const handleDelete = (widget) => {
    if (widget) {
      onDelete(widget);
      dispatch(deleteWidgets(editorSession, [widget]))
    }
  }

  const popover = (
    <Popover key={sliderData.id} bsPrefix="popover" className={styles.carouselSettingsPopover}>
      <Popover.Body className={styles.carouselSettingsPopoverBody}>
        <div className={styles.carouselSettingsPopoverContent}>
          {!isSlideLocked && <span onClick={() => { setEditable(true) }}>Rename</span>}
          {_.size(slides) < 50 &&
            <span onClick={handleClone}>Duplicate</span>
          }
          {(_.size(slides) !== 1 && !isSlideLocked) &&
            <span onClick={() => { handleDelete(sliderData.uid) }} >Delete</span>
          }
        </div>
      </Popover.Body>
    </Popover>
  );

  const handleNameChange = (e) => {
    setInputName(e.target.value);
  }

  const updateSlideName = (uid) => {
    dispatch(updateWidgetName(editorSession, uid, inputName))
    setEditable(false);
  }

  return (
    <div ref={ref} key={index} className={styles.itemWrapper} style={{ opacity }} data-handler-id={handlerId}>
      <div ref={preview} className={cx(styles.sliderItem)}>
        <div className={styles.info}>
          <i className={cx('cleverstory-drag', styles.iconDrag)} />
          {editable &&
            <input
              ref={inputRef}
              type={'text'}
              className={styles.name}
              value={inputName}
              onChange={handleNameChange}
              onBlur={() => updateSlideName(sliderData.uid)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  updateSlideName(sliderData.uid);
                }
              }}
            />
          }

          {!editable &&
            <span className={styles.name}>
              <Truncate lines={1}>{_.get(sliderData, 'name')}</Truncate>
            </span>
          }
        </div>
        {!( _.size(slides) >= 50 && isSlideLocked ) && (
          <OverlayTrigger
            rootClose={true}
            rootCloseEvent="mousedown"
            key={sliderData.id}
            trigger="click"
            placement="right"
            overlay={popover}
          >
            <i className={cx('cleverstory-more', styles.iconMore)} />
          </OverlayTrigger>
        )}
      </div>
    </div>
  )
}

export const StretchCarouselDialog = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const properties = _.get(widget, `properties.desktop`, {});
  const padding = _.get(properties, 'padding', 0);
  const stretched = _.get(properties, 'stretched', false);
  const freeSize = _.get(properties, 'free', false);
  const layout = _.get(widget, `properties.${breakpoint}.layout`);

  const pageWidget = usePageWidgetSelector(editorSession, widget.uid);
  const pageWidth = _.get(pageWidget, 'properties.desktop.container.minWidth', 1200);

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, widget.uid, 'desktop', value));
  }

  const handleSectionSize = (stretched) => {
    var frame = new Frame(layout);
    const rect = getBoundingRectFromLayout(layout);

    if (stretched) {
      frame.remove('width');
      frame.set('transform', 'translate', `0px, ${rect.y}px`);
    } else {
      frame.set('width', `${pageWidth}px`);
      frame.set('transform', 'translate', `0px, ${rect.y}px`);
    }

    handlePropertyChange({
      stretched: stretched,
      free: false,
      layout: frame.get()
    });
  }

  const selectFreeSize = () => {
    var frame = new Frame(layout);
    const rect = getBoundingRectFromLayout(layout);

    frame.set('transform', 'translate', `${rect.x}px, ${rect.y}px`);
    frame.set('width', `${pageWidth}px`);

    handlePropertyChange({
      free: !freeSize,
      stretched: false,
      layout: frame.get()
    });
  }

  return (
    <Property.Dialog className={styles.stretchCarouselDialog}
      width={230}
      target={`#${id}`}
      title={'Stretch Carousel'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property className={styles.carouselSizeProperty}>
          <div className={styles.carouselSizeContainer}>
          {!PAGE_CONTAIN_STORYBOARDS.includes(rest.storyboardType) &&
              <div className={cx(styles.carouselSize, styles.stretched, {
              [styles.active]: stretched && !freeSize
            })} onClick={() => handleSectionSize(true)}>
              <div className={styles.icon}>
                <div className={styles.inner}></div>
              </div>
              <label>Screen</label>
              </div>
            }
            <div className={cx(styles.carouselSize, {
              [styles.active]: !stretched && !freeSize
            })} onClick={() => handleSectionSize(false)}>
              <div className={styles.icon}>
                <div className={styles.inner}></div>
              </div>
              <label>Page</label>
            </div>
            <div className={cx(styles.carouselSize, styles.free, {
              [styles.active]: freeSize
            })} onClick={selectFreeSize}>
              <div className={styles.icon}>
                <div className={styles.inner}>
                  <div className={styles.pointer1} />
                  <div className={styles.pointer2} />
                  <div className={styles.pointer3} />
                  <div className={styles.pointer4} />
                </div>
              </div>
              <label>Free</label>
            </div>
          </div>
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

const renderArrowIcons = (type, direction, properties = null) => {

  var arrowIconStyles = getArrowStyles(properties);
  switch (type) {
    case 'MORDERN':
      return (
        <i style={arrowIconStyles} className={cx({ ['cleverstory-drop-down-left']: direction == 'left' }, { ['cleverstory-drop-down-right']: direction == 'right' })}></i>
      )

    case 'STANDARD':
      return (
        <i style={arrowIconStyles} className={cx({ ['cleverstory-left']: direction == 'left' }, { ['cleverstory-right']: direction == 'right' })}></i>
      )

    case 'DEFAULT':
      return (
        <i style={arrowIconStyles} className={cx({ ['cleverstory-fill-left']: direction == 'left' }, { ['cleverstory-fill-right']: direction == 'right' })}></i>
      )

    default:
      return (
        <i style={arrowIconStyles} className={cx({ ['cleverstory-left']: direction == 'left' }, { ['cleverstory-right']: direction == 'right' })}></i>
      )
  }
}

const renderDotsIcons = (data, index, isActive, lastIndex) => {
  const lastPagination = lastIndex == index;

  const dotStyles = _.get(data, 'flag') ? getPaginationStyles(data, isActive, lastPagination) : {}

  var type = _.get(data, 'style') || _.get(data, 'type')
  switch (type) {

    case 'DEFAULT':
      return (
        <i key={index} style={dotStyles} className={cx('cleverstory-pagination-default', styles.paginationIcon)} />
      )

    case 'STANDARD':
      return (
        <i key={index} style={dotStyles} className={cx('cleverstory-pagination-standard', styles.paginationIcon)} />
      )

    default:
      return (
        <i key={index} style={dotStyles} className={cx('cleverstory-pagination-standard', styles.paginationIcon)} />
      )
  }
}

const CarouselWidget = React.forwardRef((props, ref) => {
  return <CarouselWidgetView {...props} ref={ref} />
});


CarouselWidget.actions = (editorSession, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <CarouselWidgetDesktopAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
      {breakpoint === 'mobile' &&
        <CarouselWidgetMobileAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

CarouselWidget.processRequest = (widget) => {
  var updated = _.omit(widget, 'editorState');
  var background = _.get(widget, 'properties.desktop.background');

  if (!background) {
    return updated;
  }

  var media = _.get(background, 'media.id');
  if (media) {
    background = {
      ...background,
      media: media
    }
  }

  return {
    ...updated,
    properties: {
      ...updated.properties,
      desktop: {
        ...updated.properties.desktop,
        background: background
      }
    }
  }
}

CarouselWidget.moveableConfig = (editorSession, widgetProps, breakpoint) => {
  const { isFreeSize, breakPointType } = state;
  const desktop = breakpoint === 'desktop';
  const freeSize = _.get(widgetProps, 'properties.desktop.free', false);

  const flexMode = isCurrentSlideInFlexMode(editorSession, widgetProps, breakpoint);
  const flexNoResize = flexMode && !(desktop && freeSize);

  return {
    draggable: true,
    resizable: !flexNoResize,
    rotatable: false,
    cloneable: true,
    deletable: true,
    onDrag: (layout) => {
      const frame = new Frame(layout);
      const freeSize = _.get(widgetProps, 'properties.desktop.free', false);
      const translate = frame.get("transform", "translate");
      const values = _.split(translate, ",");
      const element = document.querySelector('[data-storyboard-type]');
      const storyboardType = element?.getAttribute('data-storyboard-type');
      
      var x = parseInt(values[0]);
      var y = parseInt(values[1]);

      if (PAGE_CONTAIN_STORYBOARDS.includes(storyboardType) && (!freeSize || breakPointType === 'mobile')){
        y = Math.max(0, y);
      }

      const newTranslate = `${isFreeSize && breakPointType === 'desktop' ? x : 0}px, ${y}px`;

      frame.set("transform", "translate", newTranslate);
      return frame.get();
    },
    renderDirections: flexMode
      ? (desktop && freeSize ? ['e', 'w'] : [])
      : (desktop && freeSize)
        ? ["n", "s", "ne", "nw", "se", "sw", "e", "w"]
        : ['n', 's'],
    onResize: (widget, layout, breakpoint) => {
      const stretched = _.get(widget, 'properties.desktop.stretched');
      const freeSize = _.get(widget, 'properties.desktop.free', false);

      const frame = new Frame(layout);
      if (stretched && !freeSize && breakpoint === 'desktop') {

        const margin = _.get(widget, 'properties.desktop.margin', 0);
        const width = `calc(var(--editor-width) - 2 * ${margin}px)`;
        frame.set('width', width);
      }

      if (isFreeSize && breakPointType === 'mobile'){
        // Mobile carousel is pinned to the fixed mobile frame width.
        frame.set('width', `${breakpoints.mobile.width}px`);
      }

      const flexFixedHeightResize =
        breakpoint === 'desktop' && isCurrentSlideInFlexMode(editorSession, widget, breakpoint);
      if (flexFixedHeightResize) {
        const el = document.getElementById(_.get(widget, 'uid'));
        const inner = el && el.querySelector('[data-card-inner="true"]');
        if (el && inner) {
          const chrome = Math.max(0, el.offsetHeight - inner.offsetHeight);
          const contentFloor = inner.scrollHeight + chrome;
          const dragged = pxToNumber(frame.get('height'));
          if (contentFloor > 0 && (dragged == null || dragged < contentFloor)) {
            frame.set('height', `${Math.round(contentFloor)}px`);
          }
        }
      }

      return frame.get();
    },
    onReparent: (widget, parent) => {
      const parentType = getWidgetType(editorSession, parent);
      if (parentType === PAGE) return true;
      if (!isFreeSize) return false;

      const [hasHotspotChild, childrenHasHotspot] = [
        isWidgetHasHotspotChild(editorSession, widget),
        isWidgetChildrensHasHotspotChild(editorSession, widget)
      ];
      const parentWidget = getParentContainerOfType(editorSession, widget, CONTENT_CARD);
      const denyHotspotReparent = hasHotspotChild && ![PAGE, SECTION_COLUMN, CONTAINER].includes(parentType);

      const invalidReparent =
      parentWidget ||
      denyHotspotReparent ||
      (parentType === HOTSPOT && childrenHasHotspot);

      return !invalidReparent;
    }
  }
}

function isCurrentSlideInFlexMode(editorSession, widgetProps, breakpoint) {
  try {
    const carouselId = _.get(widgetProps, 'uid');
    const widgets = _.get(reduxStore.getState(), `${editorSession}.present.widgets`) || [];
    const slides = widgets.filter(w => w.parent === carouselId);
    const currentSlideId = _.get(widgetProps, 'editorState.currentSlide');
    const slide = slides.find(w => w.uid === currentSlideId) || _.find(slides, { order: 1 });
    if (!slide) return false;
    return _.get(getCardFlexConfig(slide, breakpoint), 'flexBoxEnabled', false);
  } catch (e) {
    return false;
  }
}

function getCarouselBackground(childrenWidgets, currentSlide) {
  if (!currentSlide) {
    var defaultSlide = _.find(childrenWidgets, { order: 1 });
    return _.get(defaultSlide, 'properties.desktop.background')
  }

  const currentCarousel = _.find(childrenWidgets, { uid: currentSlide });
  return _.get(currentCarousel, 'properties.desktop.background')
}

function getPaginationStyles(properties, isActive, lastPagination) {
  var dotIconstyles = {
  };

  if (!properties && !_.get(properties, 'flag')) {
    return dotIconstyles;
  }

  //font-color
  const defaultcolor = _.get(properties, `defaultColor`, null);
  const selectedcolor = _.get(properties, `selectedColor`, null);

  const color = getColorStyles(isActive ? selectedcolor : defaultcolor);
  dotIconstyles = {
    ...dotIconstyles,
    ...color
  }

  //font-size
  const fontSizeString = _.get(properties, 'size', 8);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  dotIconstyles['fontSize'] = fontSize;

  //marginRight
  const spaceBetween = _.get(properties, 'cornerRadius', 8);
  var marginRight = 'inherit'
  if (spaceBetween && !lastPagination) {
    marginRight = parseInt(spaceBetween) + 'px';
  }

  dotIconstyles['marginRight'] = marginRight;

  return dotIconstyles;
}

function getArrowStyles(properties) {
  var arrowIconstyles = {};

  if (!properties) {
    return arrowIconstyles;
  }

  const deviceProperties = properties;
  const defaultStyles = _.get(deviceProperties, `regular`, {});

  //font-color
  const colorRgb = _.get(defaultStyles, `color`, null);
  const color = getColorStyles(colorRgb);
  arrowIconstyles = {
    ...arrowIconstyles,
    ...color
  }

  //font-size
  const fontSizeString = _.get(deviceProperties, 'size', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  arrowIconstyles['fontSize'] = fontSize;

  return arrowIconstyles;
}

function getNavigationButtonStyles(direction, properties, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  //background
  const backgroundRgb = _.get(properties, `${active}.backgroundColor`, null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = `rgba(${backgroundRgb.r}, ${backgroundRgb.g}, ${backgroundRgb.b}, ${backgroundRgb.a})`;
  }

  styles['backgroundColor'] = background;

  //border
  const border = _.get(properties, 'backgroundCornerRadius', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  //offset
  const offset = _.get(properties, 'offset');

  if (direction == 'left') {
    styles = {
      ...styles,
      left: parseInt(offset) + 'px'
    }
  }

  if (direction == 'right') {
    styles = {
      ...styles,
      right: parseInt(offset) + 'px'
    }
  }

  return styles;
}

function getStyles(widgetProps, breakpoint, container, inFlexFlow){
  const stretched = _.get(widgetProps, 'properties.desktop.stretched', false);
  const freeSize = _.get(widgetProps, 'properties.desktop.free', false);
  const margin = _.get(widgetProps, 'properties.desktop.margin', 0);
  const padding = _.get(widgetProps, `properties.${breakpoint}.padding`, 0);

  const containerProps = _.get(container, `properties.${breakpoint}.container`);

  const visibleWidth = _.get(containerProps, `minWidth`);
  const alignment = _.get(containerProps, `alignment`) || 'center';

  var multiplier = 0;
  if (alignment == 'center') {
    multiplier = 0.5;
  } else if (alignment == 'right') {
    multiplier = 1;
  }

  var widgetStyles = {
    marginLeft: `calc((100% - ${visibleWidth}px) * ${multiplier})`,
    // mirror marginLeft so React clears the flow-centering margin when leaving flow
    marginRight: 0,
    paddingTop: `${padding}px`,
    paddingBottom: `${padding}px`
  }

  if (breakpoint === 'desktop') {
    widgetStyles = {
      ...widgetStyles,
      paddingLeft: `${padding}px`,
      paddingRight: `${padding}px`,
      ...(freeSize && inFlexFlow ? {} : { width: '100%' }),
    }

    if (stretched && !freeSize) {
      const stretchedWidth = `calc(var(--editor-width) - 2 * ${margin}px)`;

      if (inFlexFlow) {
        widgetStyles = {
          ...widgetStyles,
          width: stretchedWidth,
          left: 'auto',
          marginLeft: `calc((100% - (${stretchedWidth})) / 2)`,
          marginRight: `calc((100% - (${stretchedWidth})) / 2)`,
        }
      } else {
        widgetStyles = {
          ...widgetStyles,
          width: stretchedWidth,
          left: `calc((100% - (${stretchedWidth}))/2)`
        }
      }
    }

    if(inFlexFlow && !stretched) {
      widgetStyles = {
        ...widgetStyles,
        marginLeft: '0px',
        marginRight: '0px'
      }
    }
  }

  if (breakpoint === 'mobile') {
    widgetStyles = {
      ...widgetStyles,
      width: `${breakpoints.mobile.width}px`
    }
  }

  return widgetStyles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var styles = {
    borderRadius: parseInt(value) + 'px'
  };

  return styles;
}

const Handle = React.forwardRef((props, ref) => {
  return (
    <div className={cx(styles.handle, 'item-drag-icon')} {...props} ref={ref}>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
    </div>
  );
});

export { CarouselWidget };